import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import { useMapContext } from '@components/map/MapContext';
import { TooltipComponentProps, TooltipTypeEnum } from '@components/map/tooltips/tooltip.types';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectCoordinatesUnit } from '@redux/settings/settings.selectors';
import { calculateMeasure } from '@utils/map/measure.utils';
import { convertDistanceUnitFromMeter, DistanceUnits, formatNauticalMiles, formatUnit } from '@utils/unit.utils';
import { formatCoordinatesToString } from '@utils/validation/coordinates.utils';
import DmsCoordinates from 'dms-conversion';
import { FormattedMessage } from 'react-intl';

export default function AzimuthAndDistanceTooltip({
  data,
}: Readonly<TooltipComponentProps<TooltipTypeEnum.AZIMUTH_AND_DISTANCE>>) {
  const { tooltipPosition } = data;
  const { measurePoints, isClickEnabled } = useMapContext();
  const userSettingsUnit = useAppSelector(selectCoordinatesUnit);
  const dmsCoordinates = new DmsCoordinates(tooltipPosition.latitude, tooltipPosition.longitude);
  const [lat, long] = formatCoordinatesToString(dmsCoordinates, userSettingsUnit).split(',');

  const [distance, azimuth] = calculateMeasure(tooltipPosition, measurePoints, isClickEnabled);
  const isShortDistance = measurePoints.length < 2 || (!isClickEnabled && measurePoints.length === 2);
  const segmentName = ` ${
    isClickEnabled
      ? `${measurePoints.length}-${measurePoints.length + 1}`
      : `${measurePoints.length - 1}-${measurePoints.length}`
  }`;

  return (
    <VStack gap={2} alignItems="flex-start">
      {measurePoints.length > 0 && (
        <>
          <Box>
            <Text fontSize="md" color="neutral.300">
              <FormattedMessage
                id="controls.toolMenu.measure.azimuthAndDistance.distance"
                values={{ isShortDistance }}
              />
            </Text>
            <HStack gap={1} width="100%">
              <Text textStyle="md">{formatUnit(DistanceUnits.METERS, distance)}</Text>
              <Text textStyle="md">
                {formatNauticalMiles(convertDistanceUnitFromMeter(DistanceUnits.NAUTICAL_MILES, distance))}
              </Text>
            </HStack>
          </Box>
          <Box>
            <Text fontSize="md" color="neutral.300">
              <FormattedMessage id="controls.toolMenu.measure.azimuthAndDistance.azimuth" />
              {isShortDistance ? '' : segmentName}
            </Text>
            <Text fontSize="md">{azimuth.toString().replace('.', '°')}</Text>
          </Box>
        </>
      )}
      <VStack alignItems="flex-start">
        <Text fontSize="md" color="neutral.300">
          <FormattedMessage id="controls.toolMenu.measure.azimuthAndDistance.point" />{' '}
          {isClickEnabled ? measurePoints.length + 1 : measurePoints.length}
        </Text>
        <Text fontSize="md">LAT {lat}</Text>
        <Text fontSize="md">LONG {long}</Text>
      </VStack>
    </VStack>
  );
}
